/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import * as React from 'react';

import { isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { Collapse, Drawer, Module, Node, Button } from '@msdyn365-commerce-modules/utilities';
import { AsyncResult } from '@msdyn365-commerce/core';
import { CartLine, CommerceProperty, Customer } from '@msdyn365-commerce/retail-proxy';
import { reaction } from 'mobx';
import { getCartState } from '@msdyn365-commerce/global-state';

import { trackClickMenu, trackLogin, trackUserData, trackPageType } from '../utilities/datalayer';
import { getAddToCartCategoryHierarchyAndListName, getPageType } from '../utilities/datalayer/helpers';
import { getSiteLanguageConfigFromContext, determineSiteLanguageForAbandonedCart } from '../utils/determineSiteLanguageForAbandonedCart';
import { SiteLanguageExtensionProperty, SITE_LANGUAGE_CONSTANTS } from '../../../types/site-language-for-abandoned-cart.types';
import { clearNewSignupDataLayerEvent } from '../../../common/utils/datalayer';
import { PageType } from '../utilities/datalayer/types';

/**
 * Updates the cart's site language extension property for abandoned cart email purposes.
 * This is used as a workaround to ensure the CSU API sends transaction emails
 * in the correct language when handling abandoned carts.
 *
 * @param context The commerce context
 * @param currentSiteLanguage The determined site language for email communications
 */
const addSiteLanguageExtProp = async (context: any, currentSiteLanguage: string): Promise<void> => {
    try {
        const siteLanguageExtProp: SiteLanguageExtensionProperty[] = [
            {
                Key: SITE_LANGUAGE_CONSTANTS.EXTENSION_PROPERTY_KEY,
                Value: {
                    StringValue: currentSiteLanguage
                }
            }
        ];

        const currentCartState = await getCartState(context?.actionContext);
        await currentCartState.updateExtensionProperties({
            newExtensionProperties: siteLanguageExtProp
        });
    } catch (error) {
        console.error(
            'Error in header.view.tsx - addSiteLanguageExtProp: Failed to update site language for abandoned cart emails:',
            error
        );
        throw error;
    }
};

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {
    const [isUserDataTracked, setIsUserDataTracked] = React.useState<boolean>(false);

    // It can be exported as custom hook to be used in other components
    const [isMobileDevice, setIsMobileDevice] = React.useState<boolean>(false);

    const [cartData, setCartData] = React.useState<any>(undefined);

    React.useEffect(() => {
        setCartData(props.data?.cart?.result);
    }, [props.data?.cart?.result]);

    React.useEffect(() => {
        const updateDeviceType = () => {
            if (typeof window !== 'undefined') {
                const isMobileByWidth = window.innerWidth < 992;
                setIsMobileDevice(isMobileByWidth);
            } else {
                // SSR use Commerce utilities to determine mobile state
                const browserVariant = isMobile({ variant: VariantType.Browser, context: props.context.request });
                const viewportVariant = isMobile({ variant: VariantType.Viewport, context: props.context.request });

                const mobileCheck =
                    browserVariant === 'xs' ||
                    browserVariant === 'sm' ||
                    browserVariant === 'md' ||
                    viewportVariant === 'xs' ||
                    viewportVariant === 'sm' ||
                    viewportVariant === 'md';

                setIsMobileDevice(mobileCheck);
            }
        };

        updateDeviceType();

        if (typeof window !== 'undefined') {
            const handleResize = () => updateDeviceType();
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }

        return undefined;
    }, []);

    const clickMenuLinkEventHandler = (e: any) => {
        const emojiRegex = /[\p{Emoji}]/u;
        const linkText = e.target.innerText.replace(emojiRegex, '');

        trackClickMenu(linkText, '', '');
    };

    React.useEffect(() => {
        const hamburgerButton = document.querySelector('.ms-header__nav-icon') as HTMLButtonElement;
        const collapsibleMenu = document.querySelector('.ms-header__collapsible-hamburger') as HTMLDivElement;

        if (!hamburgerButton || !collapsibleMenu) return;

        const disableButton = () => {
            hamburgerButton.setAttribute('disabled', 'true');
            hamburgerButton.style.pointerEvents = 'none';
        };

        const enableButton = () => {
            hamburgerButton.removeAttribute('disabled');
            hamburgerButton.style.pointerEvents = '';
        };

        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if ((mutation.target as Element).classList.contains('collapsing')) {
                    disableButton();
                } else {
                    enableButton();
                }
            });
        });

        observer.observe(collapsibleMenu, { attributes: true, attributeFilter: ['class'] });

        return () => {
            observer.disconnect();
        };
    }, []);

    // Effect to handle hamburger menu icon change on mobile
    React.useEffect(() => {
        if (isMobileDevice) {
            const menuButton = document.querySelector('.ms-header__nav-icon') as HTMLButtonElement;
            if (!menuButton) return;

            // Add or remove class based on menu state
            if (!props.mobileMenuCollapsed) {
                // Menu is open - change to X icon
                menuButton.classList.add('menu-open');
            } else {
                // Menu is closed - back to hamburger icon
                menuButton.classList.remove('menu-open');
            }
        }
    }, [props.mobileMenuCollapsed, isMobileDevice]);

    React.useEffect(() => {
        trackPageType(props.context);

        const currentURLPathName = props.context.request.url.requestUrl.pathname.replace(/\/$/, '');

        // Remove the session storage in PLP
        if (currentURLPathName.endsWith('.c')) {
            sessionStorage.removeItem('venchiProductCategoryHierarchy');
            sessionStorage.removeItem('venchiProductListName');
        }

        if (!props.config.hideHeader) {
            const menuLinksDesktop = document.querySelectorAll('.ms-header__desktop-view .ms-nav__list__item__link');
            const menuLinksMobile = document.querySelectorAll('nav.ms-nav.mobile-vp.mobile-view > .ms-nav__list__item__link');

            // @ts-ignore
            const menuLinks = [...menuLinksDesktop, ...menuLinksMobile];

            menuLinks.forEach(menuLink => {
                if (menuLink.parentElement) {
                    menuLink.parentElement.addEventListener('click', clickMenuLinkEventHandler);
                }
            });
        }
    }, []);

    React.useEffect(() => {
        if (props.data.cart.result?.cart) {
            // Handle GTM tracking
            if (!isUserDataTracked) {
                trackUserData(props.context, props.data.accountInformation.result, !!props.data.cart.result?.cart.LoyaltyCardId);
                trackLogin(props.context.request.user.isAuthenticated);

                setIsUserDataTracked(true);
            }

            const siteLanguageExtProp = props.data.cart.result?.cart.ExtensionProperties?.find(
                e => e.Key === SITE_LANGUAGE_CONSTANTS.EXTENSION_PROPERTY_KEY
            );

            const siteLanguageConfig = getSiteLanguageConfigFromContext(props.context);
            const currentSiteLanguage = determineSiteLanguageForAbandonedCart(siteLanguageConfig);

            if (!siteLanguageExtProp || siteLanguageExtProp.Value?.StringValue !== currentSiteLanguage) {
                addSiteLanguageExtProp(props.context, currentSiteLanguage).catch(error => {
                    console.error('Error in header.view.tsx - useEffect: Failed to update abandoned cart email language:', error);
                });
            }
        }
    }, [props.data.cart.result?.cart]);

    if (!props.config.hideHeader && typeof window !== 'undefined') {
        const [scrolled, setScrolled] = React.useState(false);
        const handleScroll = () => {
            const offset = window.scrollY;
            offset > 5 ? setScrolled(true) : setScrolled(false);
        };
        scrolled ? document.body.classList.add('headerIsSticky') : document.body.classList.remove('headerIsSticky');
        React.useEffect(() => {
            window.addEventListener('scroll', handleScroll);
            if (props.config.layout2022 === undefined || !props.config.layout2022) {
                const menuTopDesktop = document.getElementsByClassName('menu--top desktop')[0];
                const desktopMenuTop = document.getElementsByClassName('menu--top_ctn')[0];
                if (document.getElementsByClassName('menu--top mobile').length) {
                    const menuTopMobile = document.getElementsByClassName('menu--top mobile')[0];
                    const mobileMenu = document.getElementsByClassName('mobile menus')[0];
                    mobileMenu.appendChild(menuTopMobile);
                }
                desktopMenuTop.appendChild(menuTopDesktop);
            }
        }, []);
    }
    const cartLines = React.useRef<string[]>([]);
    const isCartLineInitialStatusSaved = React.useRef<boolean>(false);

    React.useEffect(() => {
        const disposeReaction = reaction(
            () => props.data.cart.result?.isProductAddedToCart!,
            async (isCartLineAdded: boolean) => {
                if (isCartLineAdded) {
                    const cartLinesToPatch = props.data.cart.result?.cart.CartLines?.filter(
                        c =>
                            !cartLines.current.includes(c.LineId!) &&
                            !c.ExtensionProperties?.filter(e => e.Key === 'venchiProductCategoryHierarchy')[0] &&
                            !c.ExtensionProperties?.filter(e => e.Key === 'venchiProductListName')[0]
                    );
                    const { categoryHierarchy, listname } = getAddToCartCategoryHierarchyAndListName();
                    if (!categoryHierarchy && !listname) {
                        return;
                    }
                    const cartLinesUpdated: CartLine[] = cartLinesToPatch
                        ? cartLinesToPatch?.map(c => {
                              let commerceProperties: CommerceProperty[] = c.ExtensionProperties ? c.ExtensionProperties : [];
                              commerceProperties = [];
                              commerceProperties.push(
                                  {
                                      Key: 'venchiProductListName',
                                      Value: { StringValue: listname }
                                  },
                                  {
                                      Key: 'venchiProductCategoryHierarchy',
                                      Value: { StringValue: categoryHierarchy }
                                  }
                              );
                              return {
                                  ...c,
                                  ExtensionProperties: commerceProperties
                              };
                          })
                        : [];
                    if (cartLinesUpdated.length) {
                        props.data.cart.result
                            ?.updateCartLines({
                                updatedCartLinesObject: cartLinesUpdated
                            })
                            .then(v => {
                                if (v.status === 'SUCCESS') {
                                    cartLines.current = props.data.cart.result?.cart.CartLines?.map(c => c.LineId!) || [];
                                }
                            });
                    }
                } else if (!isCartLineInitialStatusSaved.current) {
                    cartLines.current = props.data.cart.result?.cart.CartLines?.map(c => c.LineId!) || [];
                    isCartLineInitialStatusSaved.current = true;
                }
            }
        );

        return () => {
            // Clean up the reaction when the component unmounts
            disposeReaction(); // Dispose of the reaction to avoid memory leaks
        };
    }, []);

    if (props.config.hideHeader) {
        return <></>;
    }

    const { HeaderTag, HeaderContainer, HeaderTopBarContainer, context } = props;
    const pageType = getPageType(context);
    const isHomePage = pageType === PageType.HOME;

    const updateCartQuantity = async ({ LineId: cartLineId }: { LineId: string}, newQty: number): Promise<any> => {
        try {
            const cartResult = props.data?.cart?.result;
            if (!cartResult || !cartResult.cart) {
                console.warn('updateCartQuantity: cart result not available');
                return;
            }

            const existingCartLine = cartResult.cart.CartLines?.find((cl: any) => cl.LineId === cartLineId);
            if (!existingCartLine) {
                console.warn(`updateCartQuantity: cart line ${cartLineId} not found`);
                return;
            }

            const updatedCartLine = {
                ...existingCartLine,
                Quantity: newQty
            } as any;

            const result = await cartResult.updateCartLines({ updatedCartLinesObject: [updatedCartLine] });
            return result;
        } catch (error) {
            console.error('Error updateCartQuantity:', error);
        }
    };

    if (props.config.layout2022 === undefined || !props.config.layout2022) {
        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        <div className={'container'}>
                            <div className={'row'}>
                                <div className={'col-8 menu--top_ctn'}>
                                    <span />
                                </div>
                                <div className={'col-4 user_tools'}>
                                    {(!isMobileDevice || !isHomePage) && _renderReactFragment(props.search)}
                                    {props.preferredStore}
                                    {/* @ts-ignore */}
                                    {props?.slots?.countrySelector && props?.slots?.countrySelector}
                                    {_renderAccountBlock(props, false)}
                                </div>
                            </div>
                        </div>
                    </Node>
                    {isMobileDevice && isHomePage && (
                        <div className='search-under-header-mobile ms-header__search-container'>
                            {_renderReactFragment(
                                React.Children.map(props.search, child =>
                                    React.isValidElement(child)
                                        ? React.cloneElement(child, {
                                              className: [child.props.className, 'ms-header__search'].filter(Boolean).join(' ')
                                          })
                                        : child
                                )
                            )}
                        </div>
                    )}
                    <Node className={'ms-header__desktop-view ms-header--main-nav'}>
                        <div className={'container'}>
                            <div className={'row flex-justify--spacebetween'}>
                                <div className={'brand_ctn'}>
                                    {props.navIcon}
                                    {props.logo}
                                </div>
                                <div className={'menu--main'}>{_renderReactFragment(props.menuBar)}</div>
                                <div className={'shop_tools'}>
                                    {props.wishListIconDesktop}
                                    {React.isValidElement(props.cartIcon) &&
                                        React.cloneElement(props.cartIcon as React.ReactElement, {
                                            cartData: cartData,
                                            onUpdateQuantity: updateCartQuantity,
                                    })}
                                    {_renderReactFragment(props.siteOptions)}
                                </div>
                            </div>
                        </div>
                    </Node>
                    {_renderCollapseMenu({ ...props, hideSearchInMobileHome: isMobileDevice && isHomePage })}
                </Node>
            </Module>
        );
    } else {
        // Enhancement: replace wishlist icon with profile icon (sign-in/account) on mobile for not logged in users in selected locales
        const locale = props.context.request?.locale?.toLowerCase?.() || '';
        const allowedLocalesForProfileReplacement = new Set<string>(['it', 'en-gb', 'fr', 'de', 'en-us']);
        const isNotLoggedIn = !props.context.request.user?.isAuthenticated;
        const shouldReplaceWishlistWithProfileMobile = isNotLoggedIn && allowedLocalesForProfileReplacement.has(locale);
        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node className={`container ${isMobileDevice ? 'container-mobile-topbar' : ''}`}>
                        <Node className={`ms-header__topbar ${isMobileDevice ? 'mobile-TopBar' : 'desktop-TopBar'}`}>
                            <Node className={'flex-header--start'}>
                                {isMobileDevice ? (
                                    <>
                                        {props.navIcon}
                                        {props.logo}
                                    </>
                                ) : (
                                    <>
                                        {props.preferredStore}
                                        {props?.slots?.countrySelector && props?.slots?.countrySelector}
                                        {_renderStoreLocatorButton(props)}
                                    </>
                                )}
                            </Node>
                            {!isMobileDevice && <Node className={'flex-header--center'}>{props.logo}</Node>}
                            <Node className={'flex-header--end shop_tools'}>
                                {isMobileDevice ? (
                                    <>
                                        {_renderStoreLocatorButton(props)}
                                    </>
                                ) : (
                                    <>
                                        {_renderReactFragment(props.search)}
                                        {_renderAccountBlock(props, false)}
                                    </>
                                )}
                                {isMobileDevice ? (
                                    shouldReplaceWishlistWithProfileMobile ? _renderAccountBlock(props, true) : props.wishListIconDesktop
                                ) : (
                                    props.wishListIconDesktop
                                )}
                                {React.isValidElement(props.cartIcon) &&
                                    React.cloneElement(props.cartIcon as React.ReactElement, {
                                        cartData: cartData,
                                        onUpdateQuantity: updateCartQuantity,
                                })}
                            </Node>
                        </Node>
                    </Node>
                    {isMobileDevice && _renderCollapseMenu(props)}
                    {isMobileDevice && isHomePage && <div className='search-under-header-mobile ms-header__search-container'>{_renderReactFragment(props.search)}</div>}
                    {!isMobileDevice && <Node className='ms-header__desktop-view'>{_renderReactFragment(props.menuBar)}</Node>}
                    {props?.slots?.header_gwp && props?.slots?.header_gwp}
                </Node>
            </Module>
        );
    }
};

type CollapseMenuProps = (IHeaderViewProps & IHeaderProps<{}>) & {
    hideSearchInMobileHome?: boolean;
};

function _renderCollapseMenu(props: CollapseMenuProps): JSX.Element | null {
    const { mobileMenuCollapsed, Divider, MobileMenuLinksContainer, hideSearchInMobileHome } = props;
    if (typeof window !== 'undefined') {
        mobileMenuCollapsed === true ? document.body.classList.remove('no-scroll') : document.body.classList.add('no-scroll');
    }
    //FIXME invert the condition
    if (props.config.layout2022 === undefined || !props.config.layout2022) {
        return (
            <Collapse className={'ms-header__collapsible-hamburger'} isOpen={!mobileMenuCollapsed}>
                {!hideSearchInMobileHome && _renderReactFragment(props.search)}
                <Node {...Divider} />
                <div className={'mobile menus'}>
                    <div className={'mobile menu--main'}>{_renderReactFragment(props.menuBar)}</div>
                    {_renderMobileAccountBlock(props, true)}
                    {/* @ts-ignore */}
                    {props?.slots?.countrySelector && props?.slots?.countrySelector}
                </div>
            </Collapse>
        );
    } else {
        return (
            <Collapse className='ms-header__collapsible-hamburger' isOpen={!mobileMenuCollapsed}>
                <div className={'mobile menus'}>
                    <Node {...MobileMenuLinksContainer}>
                        {!hideSearchInMobileHome && _renderReactFragment(props.search)}
                        {/* {_renderMobileAccountBlock(props, true)} */}
                    </Node>
                    <Node {...Divider} />
                    <div className={'mobile menu--main'}>{_renderReactFragment(props.menuBar)}</div>
                </div>
                <Node className={'ms-header__menu-bottom-links'}>
                    {/* //TODO remove */}
                    {_renderMobileAccountBlock(props, true)}
                    {/* {props.wishListIconMobile} */}
                    <Node {...Divider} />
                    {props?.slots?.countrySelector && props?.slots?.countrySelector}
                </Node>
            </Collapse>
        );
    }
}
const renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
    const customer = accountInformation && accountInformation.result;
    return <>{customer ? customer.FirstName || customer.Name : ''}</>;
};

function _renderMobileAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const { AccountInfoDropdownParentContainer, signOutLink, signInLink, data, accountLinks } = props;

    let enhanchedSignOutLink = null;

    if (React.isValidElement(signOutLink)) {
        enhanchedSignOutLink = React.cloneElement(signOutLink as React.ReactElement, {
            onClick: () => clearNewSignupDataLayerEvent()
        });
    }

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            'ms-header__drawer',
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );

        if (accountLinks) {
            return (
                <Drawer
                    className={accountClassName}
                    openGlyph='ms-header__drawer-open'
                    closeGlyph='ms-header__drawer-close'
                    glyphPlacement='end'
                    toggleButtonText={renderCustomerName(data.accountInformation)}
                >
                    <div>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {enhanchedSignOutLink}
                    </div>
                </Drawer>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }
    return null;
}

function _renderAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    let enhanchedSignOutLink = null;

    if (React.isValidElement(signOutLink)) {
        enhanchedSignOutLink = React.cloneElement(signOutLink as React.ReactElement, {
            onClick: () => clearNewSignupDataLayerEvent()
        });
    }

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {enhanchedSignOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }

    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </React.Fragment>
    );
}

function _renderStoreLocatorButton(props: IHeaderViewProps & IHeaderProps<{}>): JSX.Element | null {
    if (
        props.config.storeLocatorPageLink == undefined ||
        props.config.storeLocatorPageLink.destinationUrl == undefined ||
        props.config.storeLocatorLabel == undefined
    ) {
        return <></>;
    }

    return (
        <>
            <Button
                className={classnames('ms-header__storelocator-button', props.className)}
                href={props.config.storeLocatorPageLink?.destinationUrl}
                aria-label={props.config.storeLocatorLabel}
            >
                <span className='ms-header__storelocator-button__text'>{props.config.storeLocatorLabel}</span>
            </Button>
        </>
    );
}

export default headerView;
